import React from 'react';
import { Button, ButtonGroup, Modal, Text } from '@fragments';
import { IconAlertCircle, IconArrowLeft, IconArrowRight, IconArrowUpRight } from '@tabler/icons-react';
import PlanScene from '../../components/PlanScene';
import CheckoutPane from './components/CheckoutPane';
import PlanCard from '../../components/PlanCard';
import UpgradeReceipt from './components/UpgradeReceipt';
import CardBrand from '../../components/CardBrand';
import { planText } from '../../components/PlanName';
import usePlanCheckout from './hooks/usePlanCheckout';
import { PLAN_ART_OUTPUTS as OUTPUTS, planRows } from '../../enums/plans';
import { formatCredits, formatDay, formatMoney } from '../../data/payments';
import './styles.scss';

const brand = name => (name ? name.charAt(0).toUpperCase() + name.slice(1) : '');

// Upgrade to a plan (`kind: subscription`) or buy credit packs (`kind: credits`). A plan
// offer compares cards side by side: the creator's paid plan (if any) and every plan above
// it, the top one featured; each card pays for itself (Stripe's embedded checkout, or one
// click on the card on file for a subscriber, at the previewed amount). Packs and the
// later steps are built like the feature previews: copy on the left, an animated scene in
// the glass backdrop on the right, where the checkout takes over once the payment starts.
export default function PlanDialog({ studio: s, kind, plan: initialPlan, quantity, reason }) {
  const { t } = s;
  const p = s.payments;
  const flow = usePlanCheckout({ studio: s, kind, plan: initialPlan, quantity });
  if (!p.enabled) return null;
  const plan = flow.plan;
  const locale = s.locale.replace('_', '-');
  const credits = kind === 'credits';
  const money = amount => formatMoney(amount, p.currency, locale);
  const count = units => formatCredits(units, locale);
  const monthly = credits ? 0 : p.planInfo(plan)?.monthly_credits ?? 0;
  const packUnits = flow.quantity * (p.pack?.credits_per_pack ?? 0);
  const total = credits ? money(p.packPrice === null ? null : flow.quantity * p.packPrice) : money(p.priceOf(plan));
  const card = p.subscription?.payment_method;
  const planName = credits ? t('creditsPackEyebrow') : t(`planName_${plan}`);
  const close = () => s.setModal(null);
  const checkingOut = flow.step === 'checkout';
  const comparing = !credits && flow.step === 'offer';
  const currentRank = p.planInfo(p.plan)?.rank ?? 0;
  const ladder = credits ? [] : p.catalog.plans.filter(item => item.rank > 0 && item.rank >= currentRank && item.prices.length);
  const top = p.catalog.plans.reduce((best, item) => (item.rank > (best?.rank ?? -1) ? item : best), null)?.plan;
  // The scene follows the plan on sale: the one being bought, or the featured card while comparing.
  const scenePlan = comparing && !flow.upgrading ? top : plan;
  const sceneUnits = credits ? packUnits : p.planInfo(scenePlan)?.monthly_credits ?? 0;
  const outputs = OUTPUTS[credits ? 'credits' : scenePlan].map(([id, icon]) => ({ id, icon, label: t(`planArt_${credits ? 'credits' : scenePlan}_${id}`) }));
  const scene = <PlanScene key={`${kind}-${scenePlan}-${sceneUnits}`} units={sceneUnits} locale={locale} outputs={outputs}
    unitsLabel={credits ? t('planArtPack') : t('planArtMonthly')} />;
  const preview = flow.preview?.plan === plan ? flow.preview : null;
  const due = preview && formatMoney(preview.amount_due, preview.currency, locale);

  const eyebrow = reason === 'credits' && ['offer', 'checkout'].includes(flow.step) ? t('planOutOfCredits')
    : credits ? t('creditsPackEyebrow') : t('planChooseEyebrow');
  const failure = flow.error && <div className="studio-plan-dialog__error" role="alert">
    <IconAlertCircle aria-hidden="true" />
    <Text.span contrast={8}>{t(`error_${flow.error}`, { defaultValue: t('error_generic') })}</Text.span>
  </div>;

  const rowsOf = sku => planRows(sku, t, count(p.planInfo(sku)?.monthly_credits ?? 0));
  const planCard = (sku, { badge, action } = {}) => <PlanCard key={sku} plan={sku} t={t} name={t(`planShort_${sku}`)} tagline={t(`planTagline_${sku}`)}
    price={money(p.priceOf(sku))} period={t('perMonth')} featured={sku === top} current={sku === p.plan} badge={badge} action={action} rows={rowsOf(sku)} />;

  // A subscriber pays the next plan in one click: the card on file and the previewed
  // amount are on the button itself, under how the preview reached it.
  const payNow = <>
    {(preview || !flow.error) && <UpgradeReceipt preview={preview} current={p.plan} t={t}
      money={amount => formatMoney(amount, preview.currency, locale)} date={iso => formatDay(iso, locale)} />}
    <Button className="studio-plan-pay" variant="rounded" color="inverse" fullWidth onClick={flow.begin} disabled={!preview && !flow.error}
      aria-label={due && card ? t('planPayCard', { amount: due, card: brand(card.brand), last4: card.last4 }) : undefined}>
      {card && <span className="studio-plan-pay__card"><CardBrand brand={card.brand} /><span>•••• {card.last4}</span></span>}
      <span className="studio-plan-pay__amount">{due ? t('checkoutPay', { amount: due }) : flow.error ? t('planUpgradeCta', { plan: t(`planShort_${plan}`) }) : t('planPreviewLoading')}</span>
    </Button>
    <Button size="small" variant="text" color="overlay" onClick={flow.openPortal}>{t('changeCard')}</Button>
  </>;
  const actionFor = sku => {
    if (sku === p.plan) return <span className="studio-plan-card__current"><Text.label contrast={6}>{t('planCurrentAction')}</Text.label></span>;
    if (flow.upgrading) return sku === plan ? payNow : null;
    return <Button variant="rounded" color={sku === top ? 'inverse' : 'overlay'} fullWidth endIcon={<IconArrowRight />} onClick={() => flow.start(sku)}>
      {t('planSubscribeCta', { plan: t(`planShort_${sku}`) })}
    </Button>;
  };
  const badgeFor = sku => (sku === p.plan ? t('planCurrent') : sku === top && ladder.length > 1 ? t('planRecommended') : null);

  const content = {
    offer: <>
      <Text.h2 id="studio-plan-title" contrast={9} weight={400}>{t('creditsPackTitle')}</Text.h2>
      <Text.p contrast={7}>{t('creditsPackDescription')}</Text.p>
      {p.pack && <div className="studio-plan-dialog__packs">
        <Text.label contrast={9}>{t('creditsPackChoose')}</Text.label>
        <ButtonGroup grow options={p.pack.presets.map(value => ({ value, label: count(value * p.pack.credits_per_pack) }))} value={flow.quantity} onChange={flow.choose} />
      </div>}
      <div className="studio-plan-dialog__price">
        <Text.h3 contrast={9} weight={500}>{total}</Text.h3>
        <Text.span contrast={6}>{t('creditsPackPrice', { credits: count(packUnits) })}</Text.span>
      </div>
      {failure}
      <div className="studio-plan-dialog__actions">
        <Button variant="rounded" color="inverse" endIcon={<IconArrowRight />} onClick={flow.begin}>{t('checkoutContinue')}</Button>
      </div>
      <Text.caption contrast={6}>{t(p.currency === 'brl' ? 'creditsPackNotePix' : 'creditsPackNote')}</Text.caption>
    </>,
    checkout: credits ? <>
      <Text.h2 id="studio-plan-title" contrast={9} weight={400}>{t('creditsPackSummary', { credits: count(packUnits) })}</Text.h2>
      <div className="studio-plan-dialog__price">
        <Text.h3 contrast={9} weight={500}>{total}</Text.h3>
        <Text.span contrast={6}>{t('creditsPackPrice', { credits: count(packUnits) })}</Text.span>
      </div>
      {failure}
      <Button className="studio-plan-dialog__back" variant="text" color="overlay" startIcon={<IconArrowLeft />} onClick={flow.back}>{t('creditsPackChange')}</Button>
    </> : <>
      <Text.h2 id="studio-plan-title" contrast={9} weight={400}>{planText(t, 'planSubscribeCta', planName)}</Text.h2>
      {planCard(plan)}
      {failure}
      <Button className="studio-plan-dialog__back" variant="text" color="overlay" startIcon={<IconArrowLeft />} onClick={flow.back}>{t('planBack')}</Button>
    </>,
    confirming: <>
      <Text.h2 id="studio-plan-title" contrast={9} weight={400}>{t('checkoutConfirming')}</Text.h2>
      <Text.p contrast={7} loading>{t('checkoutConfirmingHint')}</Text.p>
    </>,
    processing: <>
      <Text.h2 id="studio-plan-title" contrast={9} weight={400}>{t('checkoutProcessingTitle')}</Text.h2>
      <Text.p contrast={7}>{t('checkoutProcessing')}</Text.p>
      <div className="studio-plan-dialog__actions"><Button variant="rounded" color="inverse" onClick={close}>{t('close')}</Button></div>
    </>,
    action: <>
      <Text.h2 id="studio-plan-title" contrast={9} weight={400}>{t('planActionTitle')}</Text.h2>
      <Text.p contrast={7}>{planText(t, flow.pending?.status === 'payment_failed' ? 'planActionFailed' : 'planActionRequired', planName)}</Text.p>
      {failure}
      <div className="studio-plan-dialog__actions">
        {flow.pending?.payment_url && <Button variant="rounded" color="inverse" endIcon={<IconArrowUpRight />} onClick={() => window.open(flow.pending.payment_url, '_blank', 'noopener,noreferrer')}>{t('planOpenInvoice')}</Button>}
        <Button variant="rounded" color="overlay" onClick={flow.recheck}>{t('planPaid')}</Button>
        <Button variant="text" color="overlay" onClick={flow.openPortal}>{t('changeCard')}</Button>
      </div>
    </>,
    done: <>
      <Text.h2 id="studio-plan-title" contrast={9} weight={400}>{credits ? t('creditsDoneTitle') : planText(t, 'planDoneTitle', planName)}</Text.h2>
      <Text.p contrast={7}>{credits ? t('creditsDone', { credits: count(flow.granted ?? packUnits) }) : t('planDone', { credits: count(flow.granted ?? monthly) })}</Text.p>
      {s.credits !== null && <Text.caption contrast={6}>{t('creditsNow', { credits: count(s.credits) })}</Text.caption>}
      <div className="studio-plan-dialog__actions"><Button variant="rounded" color="inverse" endIcon={<IconArrowRight />} onClick={close}>{t('planKeepCreating')}</Button></div>
    </>,
  }[flow.step];

  const compare = <div className="studio-plan-dialog__layout is-compare">
    <div className="studio-plan-dialog__content studio-plan-dialog__intro" aria-live="polite">
      <Text.subheading contrast={6}>{eyebrow}</Text.subheading>
      <Text.h2 id="studio-plan-title" contrast={9} weight={400}>{flow.upgrading ? planText(t, `planUpgradeTitle_${plan}`, t(`planShort_${plan}`)) : t('planCompareTitle')}</Text.h2>
      <Text.p contrast={7}>{flow.upgrading ? planText(t, 'planUpgradeDescription', t(`planShort_${p.plan}`)) : t('planCompareDescription')}</Text.p>
      {failure}
      <div className="studio-plan-dialog__intro-scene" role="img" aria-label={t('planArt_alt', { plan: t(`planName_${scenePlan}`) })}>{scene}</div>
    </div>
    <div className={`studio-plan-compare is-${ladder.length}`}>{ladder.map(item => planCard(item.plan, { badge: badgeFor(item.plan), action: actionFor(item.plan) }))}</div>
    <div className="studio-plan-dialog__aside">
      <Button variant="text" color="overlay" onClick={() => p.openCredits(null, reason)}>{t('planPreferPacks')}</Button>
      <Text.caption contrast={6}>{t('planRenewNote')}</Text.caption>
    </div>
  </div>;

  // Plans take the whole viewport (less a 15px margin); packs stay a compact dialog.
  return <Modal open variant="flat" size={credits ? 960 : 1040} closeLabel={t('close')} onClose={close} aria-labelledby="studio-plan-title"
    className={`studio-dialog studio-glass-dialog studio-plan-dialog is-${flow.step}${comparing ? ' is-comparing' : ''}${credits ? '' : ' is-full'}`}
    backdrop={comparing ? <div className="studio-plan-dialog__ambient" /> : <div className="studio-plan-dialog__scene">{scene}</div>}>
    {comparing ? compare : <div className="studio-plan-dialog__layout">
      <div className="studio-plan-dialog__content" aria-live="polite">
        <Text.subheading contrast={6}>{eyebrow}</Text.subheading>
        {content}
      </div>
      {checkingOut
        ? <div className="studio-plan-dialog__checkout">
          <CheckoutPane checkout={flow.checkout} publishableKey={p.catalog.publishable_key} total={total} onComplete={flow.complete} t={t} />
        </div>
        : <div className="studio-plan-dialog__visual" role="img" aria-label={t(credits ? 'planArtPack_alt' : 'planArt_alt', { plan: planName })} />}
    </div>}
  </Modal>;
}
